// src/modules/app-views/toolbox/pages/Ai/components/ChatMode.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Card, Typography, theme } from 'antd';
import {
  CloseCircleOutlined,
  BulbOutlined,
  SearchOutlined,
  DollarOutlined,
  TagsOutlined,
  SettingOutlined,
  ToolOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import useZustand from 'hooks/useZustand';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';
import translateX from 'utils/translateX';
import InputForm from './InputForm';
import SearchedTool from './SearchedTool';
import aiIcon from 'assets/images/ai.png';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * ChatMode - Chat interface with messages for tool cataloging
 */
const ChatMode = ({
  messages,
  formRef,
  lastFormValues,
  selectedModel,
  aiModels,
  creditsInfo,
  isProcessing,
  elapsedSeconds,
  typingProgress,
  introTypingComplete,
  catalogedToolId,
  chatContainerRef,
  messagesEndRef,
  isMobile,
  showAvatar,
  onModelChange,
  onFinish,
  onBackToWelcome,
  onIntroComplete,
  onEditTool,
  onOpenCreditsDrawer,
}) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { token } = theme.useToken();
  const storedUser = useZustand('auth', 'user');

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        ...(isMobile ? {} : { height: '100%' }),
      }}
    >
      {/* Messages Container */}
      <div
        ref={chatContainerRef}
        style={{
          flex: isMobile ? undefined : 1,
          overflowY: 'auto',
          paddingTop: 24,
          paddingBottom: 24,
          border: `1px solid ${token.colorPrimary}40`,
          borderRadius: 8,
          maxHeight: 320,
          ...(isMobile && { height: 'calc(100svh - 446px)', minHeight: 300 }),
        }}
      >
        <div
          style={{
            maxWidth: 900,
            margin: '0 auto',
            padding: '0 16px',
          }}
        >
          {messages.map((msg, idx) =>
            renderMessage(msg, idx, {
              token,
              storedUser,
              elapsedSeconds,
              typingProgress,
              introTypingComplete,
              catalogedToolId,
              chatContainerRef,
              isMobile,
              showAvatar,
              creditsInfo,
              onIntroComplete,
              onEditTool,
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Container */}
      <div>
        <div
          style={{
            padding: '24px 0 12px',
          }}
        >
          <InputForm
            formRef={formRef}
            isChatMode={true}
            lastFormValues={lastFormValues}
            selectedModel={selectedModel}
            aiModels={aiModels}
            creditsInfo={creditsInfo}
            isProcessing={isProcessing}
            isMobile={isMobile}
            onModelChange={onModelChange}
            onFinish={onFinish}
            onBackToWelcome={onBackToWelcome}
            onOpenCreditsDrawer={onOpenCreditsDrawer}
          />
        </div>
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ChatMode;

// *******************************************************************************
// HELPERS
// *******************************************************************************
/**
 * Render a single message (user or AI)
 */
function renderMessage(message, index, context) {
  const {
    token,
    storedUser,
    elapsedSeconds,
    typingProgress,
    introTypingComplete,
    catalogedToolId,
    chatContainerRef,
    isMobile,
    showAvatar,
    onIntroComplete,
    onEditTool,
  } = context;

  const isUser = message.role === 'user';
  const isThinking = message.status === 'thinking';

  // Mobile layout: vertical with centered avatar
  if (isMobile) {
    return (
      <div
        key={index}
        style={{
          marginBottom: 24,
        }}
      >
        {/* Avatar centered */}
        <Flex justify="center" style={{ marginBottom: 8 }}>
          {isUser ? (
            <SimpleAvatar
              src={
                storedUser?.avatar?.base64Thumb ||
                storedUser?.avatar?.thumbUrl ||
                storedUser?.avatar?.storageUrl
              }
              name={storedUser?.name}
              size={36}
              shape="circle"
              backColor={storedUser?.backColor}
              foreColor={storedUser?.foreColor}
            />
          ) : (
            <div
              className={isThinking ? 'ai-icon-thinking' : ''}
              style={{
                width: 36,
                height: 36,
                backgroundColor: token.colorPrimary,
                WebkitMaskImage: `url(${aiIcon})`,
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskImage: `url(${aiIcon})`,
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
              }}
            />
          )}
        </Flex>

        {/* Content below avatar */}
        {!isUser && isThinking ? (
          renderThinkingPhase(elapsedSeconds, true)
        ) : (
          <Card
            variant={isUser ? 'outlined' : 'borderless'}
            className="no-shadow"
            size="small"
            styles={
              !isUser && {
                body: {
                  padding: '12px 0',
                },
              }
            }
            style={{
              borderRadius: 12,
              width: '100%',
            }}
          >
            {isUser
              ? renderUserMessage(message)
              : renderAiMessage(message, {
                  typingProgress,
                  introTypingComplete,
                  catalogedToolId,
                  chatContainerRef,
                  showAvatar,
                  onIntroComplete,
                  onEditTool,
                })}
          </Card>
        )}
      </div>
    );
  }

  // Desktop layout: horizontal with avatar on side
  return (
    <div
      key={index}
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        marginBottom: 24,
        paddingLeft: isUser ? 60 : 0,
        paddingRight: isUser ? 0 : 60,
      }}
    >
      <Flex gap={12} align="flex-start" style={{ maxWidth: '100%' }}>
        {!isUser && (
          <div
            className={isThinking ? 'ai-icon-thinking' : ''}
            style={{
              position: 'relative',
              width: 36,
              height: 36,
              flexShrink: 0,
              backgroundColor: token.colorPrimary,
              WebkitMaskImage: `url(${aiIcon})`,
              WebkitMaskSize: 'contain',
              WebkitMaskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
              maskImage: `url(${aiIcon})`,
              maskSize: 'contain',
              maskRepeat: 'no-repeat',
              maskPosition: 'center',
            }}
          />
        )}

        {!isUser && isThinking ? (
          renderThinkingPhase(elapsedSeconds, false)
        ) : (
          <Card
            variant={isUser ? 'outlined' : 'borderless'}
            className="no-shadow"
            size="small"
            styles={{
              body: {
                padding: '12px 16px',
              },
            }}
            style={{
              borderRadius: 12,
              maxWidth: '100%',
              width: !isUser ? '100%' : 'auto',
            }}
          >
            {isUser
              ? renderUserMessage(message)
              : renderAiMessage(message, {
                  typingProgress,
                  introTypingComplete,
                  catalogedToolId,
                  chatContainerRef,
                  showAvatar,
                  onIntroComplete,
                  onEditTool,
                })}
          </Card>
        )}

        {isUser && (
          <SimpleAvatar
            src={
              storedUser?.avatar?.base64Thumb ||
              storedUser?.avatar?.thumbUrl ||
              storedUser?.avatar?.storageUrl
            }
            name={storedUser?.name}
            size={36}
            shape="circle"
            backColor={storedUser?.backColor}
            foreColor={storedUser?.foreColor}
            style={{ flexShrink: 0 }}
          />
        )}
      </Flex>
    </div>
  );
}

/**
 * Render user message content
 */
function renderUserMessage(message) {
  return (
    <div>
      <div style={{ fontWeight: 500, marginBottom: 4 }}>{translateX('catalog_tool')}:</div>
      <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>
    </div>
  );
}

/**
 * Render AI message content based on status
 */
function renderAiMessage(message, context) {
  const {
    typingProgress,
    introTypingComplete,
    catalogedToolId,
    chatContainerRef,
    isMobile,
    showAvatar,
    creditsInfo,
    onIntroComplete,
    onEditTool,
  } = context;

  if (message.status === 'error') {
    return renderErrorMessage(message);
  }

  if (message.status === 'success' && message.tool) {
    return (
      <SearchedTool
        message={message}
        typingProgress={typingProgress}
        introTypingComplete={introTypingComplete}
        catalogedToolId={catalogedToolId}
        chatContainerRef={chatContainerRef}
        isMobile={isMobile}
        showAvatar={showAvatar}
        creditsInfo={creditsInfo}
        onIntroComplete={onIntroComplete}
        onEditTool={onEditTool}
      />
    );
  }

  return <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>;
}

/**
 * Render error message with details
 */
function renderErrorMessage(message) {
  const { errorDetails } = message;

  const explanation = errorDetails?.explanationKey
    ? translateX(errorDetails.explanationKey)
    : errorDetails?.explanationFallback || translateX('unexpected_error_occurred');

  const suggestions = (errorDetails?.suggestionKeys || []).map(key => translateX(key));

  return (
    <div style={{ width: '100%' }}>
      {/* Error header */}
      <Flex gap={8} align="center" style={{ marginBottom: 12 }}>
        <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
        <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
          {translateX('error_cataloging_tool')}
        </span>
      </Flex>

      {/* Error explanation */}
      <div
        style={{
          padding: 16,
          backgroundColor: 'var(--ant-color-error-bg)',
          borderRadius: 8,
          border: '1px solid var(--ant-color-error-border)',
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <Text strong>{translateX('what_happened')}:</Text>
          <div style={{ marginTop: 4 }}>{explanation}</div>
        </div>

        {errorDetails?.technicalMessage && (
          <div style={{ marginBottom: 12 }}>
            <Text strong>{translateX('technical_details')}:</Text>
            <div
              style={{
                marginTop: 4,
                fontFamily: 'monospace',
                fontSize: 12,
                color: 'var(--ant-color-text-secondary)',
              }}
            >
              {errorDetails.technicalMessage}
            </div>
          </div>
        )}

        {suggestions.length > 0 && (
          <div>
            <Text strong>{translateX('what_you_can_do')}:</Text>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
              {suggestions.map((suggestion, idx) => (
                <li key={idx} style={{ marginBottom: 4 }}>
                  {suggestion}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {translateX('try_again_hint')}
        </Text>
      </div>
    </div>
  );
}

/**
 * Thinking phases - each phase corresponds to a tool data section.
 * Derived from elapsedSeconds (pure function, no extra state needed).
 */
const THINKING_PHASES = [
  { from: 0,  key: 'ai_phase_thinking',                 icon: BulbOutlined },
  { from: 5,  key: 'ai_phase_analyzing_tool',           icon: SearchOutlined },
  { from: 13, key: 'ai_phase_classifying_tool',         icon: TagsOutlined },
  { from: 23, key: 'ai_phase_checking_specs',           icon: SettingOutlined },
  { from: 33, key: 'ai_phase_searching_prices',         icon: DollarOutlined },
  { from: 43, key: 'ai_phase_analyzing_usage',          icon: ToolOutlined },
  { from: 53, key: 'ai_phase_checking_maintenance',     icon: ClockCircleOutlined },
  { from: 63, key: 'ai_phase_compiling_references',     icon: GlobalOutlined },
  { from: 73, key: 'ai_phase_finalizing',               icon: CheckCircleOutlined },
];

/**
 * Get current thinking phase based on elapsed seconds
 */
function getThinkingPhase(seconds) {
  for (let i = THINKING_PHASES.length - 1; i >= 0; i--) {
    if (seconds >= THINKING_PHASES[i].from) return THINKING_PHASES[i];
  }
  return THINKING_PHASES[0];
}

/**
 * Render the thinking state with rotating phase messages and shimmer effect
 */
function renderThinkingPhase(elapsedSeconds, isMobile) {
  const phase = getThinkingPhase(elapsedSeconds);
  const PhaseIcon = phase.icon;

  return (
    <Flex
      align="center"
      justify={isMobile ? 'center' : 'flex-start'}
      style={{
        minHeight: 36,
        paddingLeft: isMobile ? 0 : 16,
      }}
    >
      <span className="shimmer-text" style={{ fontSize: 14 }}>
        <PhaseIcon style={{ fontSize: 14 }} />
        <span>{translateX(phase.key)}... {elapsedSeconds}s</span>
      </span>
    </Flex>
  );
}
